import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore, RouteStore } from 'bernie-core';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';

import { GalleryViewModel } from 'src/common/models';

import { ToolbarType, UitkToolbar } from 'uitk-react-toolbar';
import { UitkDialogAnimationType, UitkDialogContent, UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkMediaGalleryThumbnailView } from 'uitk-react-media-gallery';
import { UitkTab, UitkTabs, UitkTabsType } from 'uitk-react-tabs';

interface GalleryProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
  galleryDialogCloseCallback?: () => void;
  galleryDialogOpenCallback?: () => void;
  galleryName: string;
  galleryTitle?: string;
  gallery: GalleryViewModel;
  onIndexChange: (index: number) => void;
  onCategoryImageIndexChanged: (index: number) => void;
  trackingRfrr: string;
  routeStore?: RouteStore;
  id?: number;
  selectedCategory?: string;
}

interface GalleryState {
  showDialog: boolean;
}

export interface ConditionalCategoriesViewProps {
  gallery: any;
  selectedTab: string;
  onTabSelect: (selectedTab: string) => void;
  onImageSelected: (index: number) => void;
}

export const ConditionalCategoriesView = ({ gallery, selectedTab, onTabSelect, onImageSelected }: ConditionalCategoriesViewProps) => {
  if (gallery.categories && gallery.categories.length > 0) {
    return (
      <UitkTabs selectedTab={selectedTab} onTabSelect={onTabSelect} tabsType={UitkTabsType.NATURAL}>
        {gallery.categories.map(category => (
          <UitkTab name={category.name} key={category.id}>
            <UitkMediaGalleryThumbnailView images={category.images} onImageSelected={onImageSelected} />
          </UitkTab>
        ))}
      </UitkTabs>
    );
  }

  return <UitkMediaGalleryThumbnailView images={gallery.images} onImageSelected={onImageSelected} />;
};

@withLocalization
@inject('currentHotel', 'routeStore', 'analytics')
@observer
export class GalleryThumbnailDialog extends React.Component<GalleryProps, GalleryState> {
  constructor(props) {
    super(props);

    this.state = { showDialog: false };
    this.onImageSelected = this.onImageSelected.bind(this);
    this.openPhotoGalleryDialog = this.openPhotoGalleryDialog.bind(this);
    this.onTabSelect = this.onTabSelect.bind(this);
  }

  public onImageSelected(index: number) {
    const { analytics, currentHotel, gallery, onCategoryImageIndexChanged, onIndexChange } = this.props;
    const { selectedGalleryCategory } = currentHotel;
    if (selectedGalleryCategory) {
      const { id, images } = selectedGalleryCategory;
      const subjectId = images && images.length > 0 && images[index] && images[index].subjectId ? images[index].subjectId : null;
      analytics.clickTracker(
        `.ClickThumbnailImage.${id}.${subjectId}.${index}`,
        'Launch Thumbnail Gallery From Image Click',
        null,
        () => null
      )();
      onCategoryImageIndexChanged(index);
      if (selectedGalleryCategory.id === CurrentHotelStore.DEFAULTS.selectedGalleryCategoryId) {
        onIndexChange(index);
      }
    } else {
      const { images } = gallery;
      const subjectId = images && images.length > 0 && images[index] && images[index].subjectId ? images[index].subjectId : null;
      analytics.clickTracker(`.ClickThumbnailImage.${subjectId}.${index}`, 'Launch Thumbnail Gallery From Image Click', null, () => null)();
      onIndexChange(index);
    }

    this.openPhotoGalleryDialog();
  }

  private openPhotoGalleryDialog(): void {
    this.props.routeStore.updateSearch({ pwaDialog: `photoGallery-${this.props.id}` });
    this.props.analytics.clickTracker('.LaunchMainPhotoGallery', 'Launch Photo Gallery From Thumbnail Image Click', null, () => null)();
  }

  public componentDidMount(): void {
    this.setState({ showDialog: true });
  }

  public onTabSelect(selectedTab: string): void {
    const { analytics, currentHotel, gallery, trackingRfrr } = this.props;
    const { categories } = gallery;
    const selectedCategory = categories.find(x => x.name === selectedTab);
    const tabOrder = categories.findIndex(x => x.name === selectedTab);
    analytics.trackEvent(`${trackingRfrr}.${selectedCategory.id}.${tabOrder}`, 'Selected gallery tab', analytics.omnitureData);
    currentHotel.setSelectedGalleryCategoryId(selectedCategory.id);
  }

  public render(): JSX.Element {
    const { analytics, currentHotel, galleryTitle, galleryDialogCloseCallback, gallery, l10n, trackingRfrr } = this.props;

    const selectedCategory = currentHotel.selectedGalleryCategory;
    const selectedTab = selectedCategory ? selectedCategory.name : null;

    const toolBarAttributes = {
      iconLabel: l10n.formatText('hotels.infosite.amenities.cancelButton.a11yLabel'),
      type: ToolbarType.BACK,
      header: galleryTitle
    };

    const toolbarAnalytics = analytics.setupTracker(trackingRfrr, 'Close thumbnail gallery dialog');

    return (
      <UitkFullscreenDialog dialogShow={this.state.showDialog} animation={UitkDialogAnimationType.FADE}>
        <UitkToolbar {...toolBarAttributes} analytics={toolbarAnalytics} action={galleryDialogCloseCallback} />
        <UitkDialogContent className="thumbnail-dialog" zeroPadding={true}>
          <ConditionalCategoriesView
            gallery={gallery}
            selectedTab={selectedTab}
            onImageSelected={this.onImageSelected}
            onTabSelect={this.onTabSelect}
          />
        </UitkDialogContent>
      </UitkFullscreenDialog>
    );
  }
}
